import React, { useEffect, useMemo, useState } from "react";
import { Modal, Button, Form, Row, Col, Badge, Spinner } from "react-bootstrap";
import { showConfirm } from "react-confirm-prompt";
import adminUserBranchApi from "../../services/adminUserBranchApi";

const cardStyle = {
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    padding: "10px 12px",
    background: "#fff",
    height: "100%",
};

const AssignDeliveryBranchesModal = ({
    show,
    onHide,
    user,
    branches = [],
    onSaved,
}) => {
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [selectedBranchIds, setSelectedBranchIds] = useState([]);
    const [search, setSearch] = useState("");

    useEffect(() => {
        if (!show || !user?.id) return;

        const loadData = async () => {
            setLoading(true);
            try {
                const response = await adminUserBranchApi.getAssignedBranches({ userId: user.id });
                const data = response?.data?.data || response?.data || response;
                setSelectedBranchIds(data?.branchIds || []);
            } catch (error) {
                showConfirm("Error", {
                    description: error?.message || "Failed to load assigned branches",
                    type: "warning",
                    hideCancel: true,
                });
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, [show, user?.id]);

    const filteredBranches = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return branches;

        return branches.filter((branch) =>
            String(branch.branchName || "")
                .toLowerCase()
                .includes(q)
        );
    }, [branches, search]);

    const toggleBranch = (branchId) => {
        setSelectedBranchIds((prev) =>
            prev.includes(branchId)
                ? prev.filter((id) => id !== branchId)
                : [...prev, branchId]
        );
    };

    const selectAllFiltered = () => {
        const ids = filteredBranches.map((item) => Number(item.branchId));
        setSelectedBranchIds((prev) => [...new Set([...prev, ...ids])]);
    };

    const clearAll = () => setSelectedBranchIds([]);

    const handleSave = async () => {
        if (!user?.id) return;

        setSaving(true);
        try {
            const result = await adminUserBranchApi.saveAssignedBranches({
                userId: user.id,
                branchIds: selectedBranchIds,
            });

            const ok = result?.status === "SUCCESS" || result?.data?.status === "SUCCESS";
            if (!ok) {
                throw new Error(result?.message || "Failed to save branch assignment");
            }

            showConfirm("Success", {
                description: result?.message || "Branches assigned successfully.",
                type: "success",
                hideCancel: true,
            });

            onSaved?.(selectedBranchIds);
            onHide?.();
        } catch (error) {
            showConfirm("Error", {
                description: error?.message || "Failed to save assigned branches",
                type: "warning",
                hideCancel: true,
            });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal show={show} onHide={() => !saving && onHide?.()} size="lg" centered>
            <Modal.Header closeButton>
                <Modal.Title>Assign Delivery Branches</Modal.Title>
            </Modal.Header>

            <Modal.Body>
                <div className="mb-3">
                    <div><b>User:</b> {user?.name}</div>
                    <div><b>Username:</b> {user?.username}</div>
                </div>

                <Row className="mb-3">
                    <Col md={8}>
                        <Form.Control
                            placeholder="Search branch name..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </Col>
                    <Col md={4} className="d-flex gap-2 mt-2 mt-md-0">
                        <Button variant="outline-primary" className="w-100" onClick={selectAllFiltered}>
                            Select Filtered
                        </Button>
                        <Button variant="outline-secondary" className="w-100" onClick={clearAll}>
                            Clear All
                        </Button>
                    </Col>
                </Row>

                <div className="mb-3">
                    <Badge bg="success" pill>
                        Selected: {selectedBranchIds.length}
                    </Badge>
                </div>

                {loading ? (
                    <div className="text-center py-4">
                        <Spinner animation="border" />
                        <div className="mt-2">Loading assigned branches...</div>
                    </div>
                ) : (
                    <div style={{ maxHeight: 420, overflowY: "auto", paddingRight: 4 }}>
                        <Row className="g-3">
                            {filteredBranches.map((branch) => {
                                const checked = selectedBranchIds.includes(Number(branch.branchId));
                                return (
                                    <Col md={6} key={branch.branchId}>
                                        <div style={cardStyle}>
                                            <Form.Check
                                                type="checkbox"
                                                id={`branch-${branch.branchId}`}
                                                checked={checked}
                                                onChange={() => toggleBranch(Number(branch.branchId))}
                                                label={
                                                    <div>
                                                        <div style={{ fontWeight: 600 }}>
                                                            {branch.branchName}
                                                        </div>
                                                        <div style={{ fontSize: 12, color: "#6b7280" }}>
                                                            Branch ID: {branch.branchId}
                                                        </div>
                                                    </div>
                                                }
                                            />
                                        </div>
                                    </Col>
                                );
                            })}

                            {!filteredBranches.length && (
                                <Col xs={12}>
                                    <div className="text-center text-muted py-4">
                                        No branches found
                                    </div>
                                </Col>
                            )}
                        </Row>
                    </div>
                )}
            </Modal.Body>

            <Modal.Footer>
                <Button variant="secondary" onClick={onHide} disabled={saving}>
                    Cancel
                </Button>
                <Button variant="primary" onClick={handleSave} disabled={saving}>
                    {saving ? "Saving..." : "Save Branches"}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default AssignDeliveryBranchesModal;